/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * COMPONENTE: ListaTarefas
 * VERSÃO:  1.0.0
 * ------------------------------------------------------------
 * Lista as tarefas do laudo/imóvel com:
 *   ✔ Badges de prioridade (ALTISSIMA..SEM_PRIORIDADE)
 *   ✔ Badges de categoria (CONTATO, DIVERGENCIA, ...)
 *   ✔ Ações: concluir, reabrir, agendar vistoria
 *   ✔ Filtros: categoria, status, prioridade
 *   ✔ Estado vazio com CTA para criar tarefa
 *   ✔ Loading, erros e feedback visual
 * ============================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import DiligenciaService, {
  CATEGORIA,
  PRIORIDADE,
  PESO_PRIORIDADE,
} from '../../services/DiligenciaService';

/* ============================================================
   CONFIGURAÇÃO VISUAL
   ============================================================ */

const PRIORIDADE_CONFIG = {
  [PRIORIDADE.ALTISSIMA]: {
    label: 'Altíssima',
    badge: 'bg-red-600 text-white',
    border: 'border-l-red-600',
    dot: 'bg-red-600',
  },
  [PRIORIDADE.ALTA]: {
    label: 'Alta',
    badge: 'bg-orange-500 text-white',
    border: 'border-l-orange-500',
    dot: 'bg-orange-500',
  },
  [PRIORIDADE.MEDIA]: {
    label: 'Média',
    badge: 'bg-yellow-400 text-slate-900',
    border: 'border-l-yellow-400',
    dot: 'bg-yellow-400',
  },
  [PRIORIDADE.BAIXA]: {
    label: 'Baixa',
    badge: 'bg-sky-500 text-white',
    border: 'border-l-sky-500',
    dot: 'bg-sky-500',
  },
  [PRIORIDADE.SEM_PRIORIDADE]: {
    label: 'Sem prioridade',
    badge: 'bg-slate-300 text-slate-700',
    border: 'border-l-slate-300',
    dot: 'bg-slate-400',
  },
};

const CATEGORIA_CONFIG = {
  [CATEGORIA.CONTATO]:     { label: 'Contato',      icon: 'phone' },
  [CATEGORIA.DIVERGENCIA]: { label: 'Divergência',  icon: 'alert-triangle' },
  [CATEGORIA.VERIFICACAO]: { label: 'Verificação',  icon: 'check-circle' },
  [CATEGORIA.LEMBRETE]:    { label: 'Lembrete',     icon: 'bell' },
  [CATEGORIA.MANUAL]:      { label: 'Manual',       icon: 'user' },
  [CATEGORIA.SISTEMA]:     { label: 'Sistema',      icon: 'cpu' },
};

const STATUS_CONFIG = {
  PENDENTE:     { label: 'Pendente',     badge: 'bg-amber-100 text-amber-800' },
  CONCLUIDA:    { label: 'Concluída',    badge: 'bg-emerald-100 text-emerald-800' },
  CANCELADA:    { label: 'Cancelada',    badge: 'bg-slate-200 text-slate-500' },
  REAGENDAR:    { label: 'Reagendar',    badge: 'bg-purple-100 text-purple-800' },
  IMPEDIDA:     { label: 'Impedida',     badge: 'bg-red-100 text-red-800' },
  EM_ANDAMENTO: { label: 'Em andamento', badge: 'bg-blue-100 text-blue-800' },
  AGENDADO:     { label: 'Agendado',     badge: 'bg-emerald-100 text-emerald-800' },
};

/* ============================================================
   HELPERS
   ============================================================ */

function formatarDataHora(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function prazoEstaVencido(prazoIso, status) {
  if (!prazoIso) return false;
  if (['CONCLUIDA', 'CANCELADA'].includes(status)) return false;
  return new Date(prazoIso) < new Date();
}

/* ============================================================
   SUBCOMPONENTES
   ============================================================ */

function BadgePrioridade({ prioridade }) {
  const cfg = PRIORIDADE_CONFIG[prioridade] || PRIORIDADE_CONFIG[PRIORIDADE.SEM_PRIORIDADE];
  return (
    <span className={`${cfg.badge} px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider`}>
      {cfg.label}
    </span>
  );
}

function BadgeStatus({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, badge: 'bg-slate-200 text-slate-600' };
  return (
    <span className={`${cfg.badge} px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider`}>
      {cfg.label}
    </span>
  );
}

function BadgeCategoria({ categoria }) {
  const cfg = CATEGORIA_CONFIG[categoria] || { label: categoria, icon: 'tag' };
  return (
    <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider">
      {cfg.label}
    </span>
  );
}

/* ============================================================
   CARD DE TAREFA
   ============================================================ */

function CardTarefa({ tarefa, onConcluir, onReabrir, loading }) {
  const cfg = PRIORIDADE_CONFIG[tarefa.prioridade] || PRIORIDADE_CONFIG[PRIORIDADE.SEM_PRIORIDADE];
  const vencida = prazoEstaVencido(tarefa.prazo, tarefa.status);
  const jaFinalizada = ['CONCLUIDA', 'CANCELADA'].includes(tarefa.status);

  return (
    <div
      className={`
        bg-white rounded-3xl p-6 border-l-[8px] ${cfg.border}
        shadow-sm hover:shadow-lg transition-all
        ${jaFinalizada ? 'opacity-60' : ''}
      `}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <BadgePrioridade prioridade={tarefa.prioridade} />
            <BadgeCategoria categoria={tarefa.categoria} />
            <BadgeStatus status={tarefa.status} />
          </div>
          <h4 className={`font-black text-sm uppercase tracking-tight ${jaFinalizada ? 'line-through text-slate-400' : 'text-slate-900'}`}>
            {tarefa.titulo}
          </h4>
          {tarefa.descricao && (
            <p className="text-[11px] text-slate-500 font-medium mt-1 leading-relaxed">
              {tarefa.descricao}
            </p>
          )}
        </div>

        <span className="text-[10px] font-black text-slate-400 shrink-0">
          #{tarefa.id}
        </span>
      </div>

      <div className="flex items-center justify-between gap-4 mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-4 text-[10px] font-black text-slate-500">
          <span className={vencida ? 'text-red-600' : ''}>
            ⏰ {formatarDataHora(tarefa.prazo)}
            {vencida && <span className="ml-1 text-red-600">(VENCIDA)</span>}
          </span>
          {tarefa.imovel_id && (
            <span className="text-slate-400">Imóvel #{tarefa.imovel_id}</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!jaFinalizada && (
            <button
              onClick={() => onConcluir(tarefa.id)}
              disabled={loading}
              className="bg-emerald-600 text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-wider hover:bg-emerald-700 transition-all disabled:opacity-50"
            >
              ✓ Concluir
            </button>
          )}
          {tarefa.status === 'CONCLUIDA' && (
            <button
              onClick={() => onReabrir(tarefa.id)}
              disabled={loading}
              className="bg-slate-900 text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-wider hover:bg-slate-700 transition-all disabled:opacity-50"
            >
              ↺ Reabrir
            </button>
          )}
          {tarefa.status === 'CANCELADA' && (
            <button
              onClick={() => onReabrir(tarefa.id)}
              disabled={loading}
              className="bg-slate-400 text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-wider hover:bg-slate-600 transition-all disabled:opacity-50"
            >
              ↺ Reabrir
            </button>
          )}
        </div>
      </div>

      {tarefa.executado_em && (
        <p className="text-[9px] font-black text-emerald-600 uppercase mt-2">
          Concluída em {formatarDataHora(tarefa.executado_em)}
        </p>
      )}
      {tarefa.erro && !jaFinalizada && (
        <p className="text-[9px] font-black text-red-600 uppercase mt-2">
          ⚠ {tarefa.erro}
        </p>
      )}
    </div>
  );
}

/* ============================================================
   COMPONENTE PRINCIPAL
   ============================================================ */

export default function ListaTarefas({
  codigo,              // ex: 'MH-2026-0006'
  imovelId = null,     // opcional — filtra por imóvel
  imovelUuid = null,   // opcional
  onNovaTarefa,        // callback para botão "+ Nova Tarefa"
  onAgendarVistoria,   // callback para botão "Agendar Vistoria"
}) {
  const [tarefas, setTarefas] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  /* ---------- Carregar ---------- */

  const carregar = useCallback(async () => {
    if (!codigo) return;
    setLoading(true);
    setErro(null);
    try {
      const filtros = {};
      if (imovelId)   filtros.imovel_id   = imovelId;
      if (imovelUuid) filtros.imovel_uuid = imovelUuid;
      if (filtroCategoria) filtros.categoria = filtroCategoria;
      if (filtroStatus)    filtros.status    = filtroStatus;

      const resp = await DiligenciaService.buscarTarefasBackend(codigo, filtros);
      setTarefas(resp.tarefas || []);
      setResumo({
        total: resp.total,
        por_prioridade: resp.por_prioridade,
        por_status: resp.por_status,
        por_categoria: resp.por_categoria,
      });
    } catch (e) {
      setErro(e.message || 'Falha ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  }, [codigo, imovelId, imovelUuid, filtroCategoria, filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  /* ---------- Ações ---------- */

  const handleConcluir = async (tarefaId) => {
    setActionLoading(true);
    setFeedback(null);
    try {
      await DiligenciaService.concluirTarefa(tarefaId, 'Concluída via interface');
      setFeedback({ tipo: 'sucesso', msg: 'Tarefa concluída' });
      await carregar();
    } catch (e) {
      setFeedback({ tipo: 'erro', msg: e.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReabrir = async (tarefaId) => {
    const motivo = window.prompt('Motivo da reabertura:');
    if (!motivo || !motivo.trim()) return;

    setActionLoading(true);
    setFeedback(null);
    try {
      await DiligenciaService.reabrirTarefa(tarefaId, motivo.trim());
      setFeedback({ tipo: 'sucesso', msg: 'Tarefa reaberta' });
      await carregar();
    } catch (e) {
      setFeedback({ tipo: 'erro', msg: e.message });
    } finally {
      setActionLoading(false);
    }
  };

  /* ---------- Feedback auto-dismiss ---------- */

  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(t);
  }, [feedback]);

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <div className="w-full h-full flex flex-col bg-slate-50 p-6 rounded-[2rem]">

      {/* CABEÇALHO */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-slate-200">
        <div>
          <h3 className="font-black uppercase text-slate-900 text-xl tracking-tight">
            Tarefas
          </h3>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-1">
            {imovelId ? `Imóvel #${imovelId}` : 'Laudo completo'}
          </p>
        </div>

        {resumo && (
          <div className="flex items-center gap-4 text-[10px] font-black uppercase">
            {resumo.por_prioridade?.ALTISSIMA > 0 && (
              <span className="text-red-600">
                🔴 {resumo.por_prioridade.ALTISSIMA} altíssima
              </span>
            )}
            {resumo.por_prioridade?.ALTA > 0 && (
              <span className="text-orange-500">
                🟠 {resumo.por_prioridade.ALTA} alta
              </span>
            )}
            {resumo.por_status?.PENDENTE > 0 && (
              <span className="text-slate-500">
                {resumo.por_status.PENDENTE} pendentes
              </span>
            )}
          </div>
        )}
      </div>

      {/* FILTROS + AÇÕES */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <select
          value={filtroCategoria}
          onChange={e => setFiltroCategoria(e.target.value)}
          className="bg-white border-2 border-slate-200 rounded-xl px-4 py-2 text-[10px] font-black uppercase text-slate-700"
        >
          <option value="">Categoria (todas)</option>
          {Object.keys(CATEGORIA_CONFIG).map(c => (
            <option key={c} value={c}>{CATEGORIA_CONFIG[c].label}</option>
          ))}
        </select>

        <select
          value={filtroStatus}
          onChange={e => setFiltroStatus(e.target.value)}
          className="bg-white border-2 border-slate-200 rounded-xl px-4 py-2 text-[10px] font-black uppercase text-slate-700"
        >
          <option value="">Status (todos)</option>
          {Object.keys(STATUS_CONFIG).map(s => (
            <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
          ))}
        </select>

        <div className="flex-1" />

        {onNovaTarefa && (
          <button
            onClick={onNovaTarefa}
            className="bg-slate-900 text-white px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider hover:bg-slate-700 transition-all"
          >
            + Nova Tarefa
          </button>
        )}
        {onAgendarVistoria && (
          <button
            onClick={onAgendarVistoria}
            className="bg-emerald-600 text-white px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider hover:bg-emerald-700 transition-all"
          >
            📅 Agendar Vistoria
          </button>
        )}
      </div>

      {/* FEEDBACK */}
      {feedback && (
        <div
          className={`
            mb-4 p-4 rounded-2xl text-[11px] font-black uppercase tracking-wide
            ${feedback.tipo === 'sucesso'
              ? 'bg-emerald-100 text-emerald-800 border-2 border-emerald-300'
              : 'bg-red-100 text-red-800 border-2 border-red-300'}
          `}
        >
          {feedback.tipo === 'sucesso' ? '✓ ' : '⚠ '}
          {feedback.msg}
        </div>
      )}

      {/* ERRO */}
      {erro && (
        <div className="mb-4 p-6 bg-red-50 border-2 border-red-200 rounded-2xl text-center">
          <p className="text-red-800 font-black text-xs uppercase">Erro: {erro}</p>
          <button
            onClick={carregar}
            className="mt-3 text-[10px] font-black uppercase text-red-600 underline"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* LOADING */}
      {loading && !erro && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-slate-400 font-black text-xs uppercase tracking-widest animate-pulse">
            Carregando tarefas...
          </p>
        </div>
      )}

      {/* VAZIO */}
      {!loading && !erro && tarefas.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center py-12">
          <div className="bg-slate-100 rounded-full p-8 mb-4">
            <span className="text-4xl">📋</span>
          </div>
          <p className="text-slate-500 font-black text-xs uppercase tracking-widest mb-2">
            Nenhuma tarefa encontrada
          </p>
          <p className="text-slate-400 font-medium text-[11px] mb-6">
            Crie uma tarefa manual ou agende uma vistoria
          </p>
          {onNovaTarefa && (
            <button
              onClick={onNovaTarefa}
              className="bg-slate-900 text-white px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider"
            >
              + Criar primeira tarefa
            </button>
          )}
        </div>
      )}

      {/* LISTA */}
      {!loading && !erro && tarefas.length > 0 && (
        <div className="flex-1 space-y-4 overflow-y-auto pr-2">
          {tarefas.map(t => (
            <CardTarefa
              key={t.id}
              tarefa={t}
              onConcluir={handleConcluir}
              onReabrir={handleReabrir}
              loading={actionLoading}
            />
          ))}
        </div>
      )}

    </div>
  );
}